import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from "react-redux";
import Editor from '../../../components/editor';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { announcementsSave, announcementsUpdate } from 'apiServices';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { showPmrError } from '../../../utils/swalUtils';
import { Edit, Trash2 } from 'lucide-react';
import CustomSuccessModal from '../../../components/CustomSuccessModal';


const getAnnouncementValidUptoRaw = (announcement) => {
  if (!announcement) return null;
  const value =
    announcement.valid_up_to ??
    announcement.valid_upto ??
    announcement.ValidUpTo ??
    announcement.ValidUpto ??
    announcement.validUpto;
  return value === null || value === undefined || value === '' ? null : value;
};

const parseValidUptoDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const dotNetMatch = String(value).match(/\/Date\((\d+)\)\//);
  if (dotNetMatch) {
    const parsed = new Date(parseInt(dotNetMatch[1], 10));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** Flatpickr/API format — local date-time, no UTC shift */
const VALID_UPTO_API_FORMAT = "Y-m-d\\ H:i";

/** Local date/time for API — avoids toISOString() shifting picker time to UTC */
const formatValidUptoForApi = (value) => {
  const date = value instanceof Date ? value : parseValidUptoDate(value);
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

const BlogTwo = ({ announcementToEdit, onFinish, onUpdate }) => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.apiService.login);
  const token = userData?.Token || '';
  const user_id = userData?.user_id || '';

  const isEditMode = !!announcementToEdit;
  const isEditor = isEditMode; // Use isEditor as requested

  const resolvedValidUpto = useMemo(
    () => parseValidUptoDate(getAnnouncementValidUptoRaw(announcementToEdit)),
    [announcementToEdit]
  );

  const defaultValidUpto = useMemo(() => {
    if (isEditMode) return null;
    const nextYear = new Date();
    nextYear.setFullYear(nextYear.getFullYear() + 1);
    return nextYear;
  }, [isEditMode]);

  const effectiveValidUpto = resolvedValidUpto ?? defaultValidUpto;

  // Function to clean and fix malformed image URLs
  const cleanImageUrl = useCallback((url) => {
    if (!url || typeof url !== 'string') {
      return null;
    }

    // Handle malformed URLs that contain both server path and blob URL
    if (url.includes('/AnnouncementImages/blob:')) {
      return null; // Return null to show fallback
    }

    // Remove blob URLs as they're temporary
    if (url.startsWith('blob:')) {
      return null;
    }

    // Handle multiple URL duplications
    // Extract the base URL pattern and filename
    const baseUrlPattern = /https?:\/\/[^\/]+\/AnnouncementImages\//g;
    const matches = url.match(baseUrlPattern);

    if (matches && matches.length > 1) {
      // Multiple base URLs found - extract the filename from the end
      const lastBaseUrlIndex = url.lastIndexOf('AnnouncementImages/');
      if (lastBaseUrlIndex !== -1) {
        const filename = url.substring(lastBaseUrlIndex + 'AnnouncementImages/'.length);
        // Reconstruct with just one base URL
        const cleanBaseUrl = matches[0]; // Take the first occurrence as the base
        url = cleanBaseUrl + filename;
      }
    }

    // Fix backslashes in URLs - replace \\ with /
    let cleanedUrl = url.replace(/\\\\/g, '/').replace(/\\/g, '/');

    return cleanedUrl;
  }, []);

  const [formData, setFormData] = useState({});
  const [date, setDate] = useState(null);
  const validUptoStrRef = useRef("");
  // Add validation state
  const [errors, setErrors] = useState({
    title: false,
    description: false,
    card_link: false
  });

  // Helper function to check if description is empty (handles HTML tags and whitespace)
  const isDescriptionEmpty = useCallback((description) => {
    if (!description) return true;

    // Check for embedded content like videos, iframes, images, etc.
    const hasEmbeddedContent = /<(iframe|video|audio|img|embed|object)[^>]*>/i.test(description);
    if (hasEmbeddedContent) {
      return false; // If there's embedded content, it's not empty
    }

    // Remove HTML tags and check if only whitespace remains
    const textContent = description.replace(/<[^>]*>/g, '').trim();
    return textContent === '';
  }, []);

  // Helper function to validate URL
  const isValidUrl = useCallback((url) => {
    if (!url || url.trim() === '') return true; // Allow empty URLs
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch (error) {
      return false;
    }
  }, []);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const hasExistingImage = announcementToEdit?.image_url;
    let cleanedImageUrl = null;
    let isExistingWebUrl = false;

    if (hasExistingImage) {
      // Clean the image URL first
      cleanedImageUrl = cleanImageUrl(announcementToEdit.image_url);

      // Determine if existing image is a web URL or uploaded file
      // If cleaned URL contains '/AnnouncementImages/' it's an uploaded file
      // If it's an external URL (different domain) it's a web URL
      isExistingWebUrl = cleanedImageUrl &&
        !cleanedImageUrl.includes('/AnnouncementImages/') &&
        (cleanedImageUrl.startsWith('http://') || cleanedImageUrl.startsWith('https://'));
    }

    const initialData = {
      announcement_id: announcementToEdit?.announcement_id || 0,
      title: announcementToEdit?.title || '',
      subTitle: announcementToEdit?.sub_title || '',
      image_url: isExistingWebUrl ? cleanedImageUrl : "",
      isWebUrl: isExistingWebUrl, // Only true if it's actually a web URL
      imageFile: null,
      imagePreview: cleanedImageUrl, // Use cleaned URL for preview
      description: announcementToEdit?.description || '',
      card_link: announcementToEdit?.card_link || '',
      validUpto: effectiveValidUpto,
      validUptoStr: effectiveValidUpto ? formatValidUptoForApi(effectiveValidUpto) : "",
      active: announcementToEdit?.is_active ?? announcementToEdit?.isactive ?? true,
      token: token,
      user_id: user_id,
    };

    validUptoStrRef.current = initialData.validUptoStr;
    setFormData(initialData);
    setDate(effectiveValidUpto);
  }, [announcementToEdit, token, user_id, cleanImageUrl, effectiveValidUpto]);

  const flatpickrOptions = useMemo(() => {
    const now = new Date();
    return {
      enableTime: true,
      dateFormat: VALID_UPTO_API_FORMAT,
      allowInput: false,
      minDate: now,
      disable: [
        function (pickerDate) {
          const startOfToday = new Date();
          startOfToday.setHours(0, 0, 0, 0);
          return pickerDate < startOfToday;
        }
      ],
      ...(effectiveValidUpto ? { defaultDate: effectiveValidUpto } : {}),
    };
  }, [effectiveValidUpto]);

  const formatDate = useCallback((date) => {
    if (!date) return "";
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };
    return date.toLocaleString("en-US", options);
  }, []);

  const handleEditorChange = useCallback((value) => {
    setFormData(prev => ({ ...prev, description: value }));

    // Clear validation error when user starts typing in description
    if (errors.description && !isDescriptionEmpty(value)) {
      setErrors(prev => ({ ...prev, description: false }));
    }
  }, [errors.description, isDescriptionEmpty]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => {
      const newFormData = { ...prev, [name]: type === 'checkbox' ? checked : value };

      if (name === 'isWebUrl') {
        if (checked) {
          if (prev.imagePreview && prev.imagePreview.startsWith('blob:')) {
            URL.revokeObjectURL(prev.imagePreview);
          }
          newFormData.imageFile = null;
          newFormData.imagePreview = newFormData.image_url || null;
        }
      } else if (name === 'image_url') {
        // Clean the image URL when it's entered
        const cleanedUrl = cleanImageUrl(value);
        newFormData.imagePreview = cleanedUrl || value;
      }

      return newFormData;
    });

    // Clear validation error when user starts typing
    if (name === 'title' && errors.title) {
      setErrors(prev => ({ ...prev, title: false }));
    }

    // Validate URL for card_link field
    if (name === 'card_link') {
      if (value.trim() !== '' && !isValidUrl(value)) {
        setErrors(prev => ({ ...prev, card_link: true }));
      } else {
        setErrors(prev => ({ ...prev, card_link: false }));
      }
    }
  }, [errors.title, isValidUrl]);

  const handleImageUpload = useCallback((e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Check file size (800KB = 800 * 1024 bytes)
      if (file.size > 800 * 1024) {
        showPmrError('File size should not exceed 800KB');
        return;
      }
      // Check file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        showPmrError('Only JPG, PNG and GIF files are allowed');
        return;
      }
      setFormData(prev => ({
        ...prev,
        imageFile: file,
        imagePreview: URL.createObjectURL(file)
      }));
    }
  }, []);

  // Cleanup preview URL when component unmounts or when image changes
  useEffect(() => {
    return () => {
      if (formData.imagePreview && formData.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(formData.imagePreview);
      }
    };
  }, [formData.imagePreview]);

  const handleDateChange = useCallback((selectedDates, dateStr) => {
    const selectedDate = selectedDates?.[0] ?? null;
    const apiDateStr = dateStr || (selectedDate ? formatValidUptoForApi(selectedDate) : "");
    validUptoStrRef.current = apiDateStr;
    setDate(selectedDate);
    setFormData((prev) => ({
      ...prev,
      validUpto: selectedDate,
      validUptoStr: apiDateStr,
    }));
  }, []);

  const getValidUptoPayload = useCallback(() => {
    if (validUptoStrRef.current) return validUptoStrRef.current;
    if (formData.validUptoStr) return formData.validUptoStr;
    const validUptoDate = formData.validUpto ?? date ?? effectiveValidUpto;
    return formatValidUptoForApi(validUptoDate);
  }, [formData.validUptoStr, formData.validUpto, date, effectiveValidUpto]);

  const handleSave = useCallback(() => {
    // Reset previous errors
    setErrors({ title: false, description: false, card_link: false });

    let hasErrors = false;
    const newErrors = { title: false, description: false, card_link: false };

    // Validate required fields
    if (!formData.title || !formData.title.trim()) {
      newErrors.title = true;
      hasErrors = true;
    }

    if (!formData.description || isDescriptionEmpty(formData.description)) {
      newErrors.description = true;
      hasErrors = true;
    }

    // Validate URL if provided
    if (formData.card_link && formData.card_link.trim() !== '' && !isValidUrl(formData.card_link)) {
      newErrors.card_link = true;
      hasErrors = true;
    }

    // If there are validation errors, set them and stop
    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    // Format payload to match API requirements
    const payload = {
      announcement_id: formData.announcement_id || 0,
      title: formData.title || "",
      sub_title: formData.subTitle || "",
      description: formData.description || "",
      card_link: (formData.card_link && formData.card_link.trim() !== '' && isValidUrl(formData.card_link)) ? formData.card_link : "",
      role_ids: "", // You can modify this based on your requirements
      valid_up_to: getValidUptoPayload(),
      is_active: Boolean(formData.active),
      is_web_url: Boolean(formData.isWebUrl),
      user_id: userData.user_id,
      token: token
    };
    // Handle image_url based on whether it's a web URL or uploaded file
    if (formData.imageFile) {
      // If there's a file upload, include it in the payload
      payload.imageFile = formData.imageFile;
      payload.image_url = ""; // Will be set by the server after upload
    } else if (formData.isWebUrl && formData.image_url) {
      // If it's a web URL, use the provided URL
      payload.image_url = formData.image_url;
    } else {
      payload.image_url = "";
    }
    dispatch(announcementsSave(payload))
      .then((response) => {
        if (response.payload && response.payload.return_code === 0) {
          // Refresh announcements immediately after successful save
          // if (onUpdate) {
          //   onUpdate();
          // }
          setSuccessMessage(response.payload.return_message || 'Announcement Created successfully');
          setShowSuccessModal(true);
          // Don't call onFinish immediately - let the success modal handle it
          // console.log("response.payload.return_message", response.payload.return_message);
        }
      })
      .catch((error) => {
        console.error("Error saving announcement:", error);
      });
  }, [formData, dispatch, onFinish, announcementToEdit, isDescriptionEmpty, onUpdate, userData.user_id, token, getValidUptoPayload]);

  const handleUpdate = useCallback(() => {
    // Reset previous errors
    setErrors({ title: false, description: false, card_link: false });

    let hasErrors = false;
    const newErrors = { title: false, description: false, card_link: false };

    // Validate required fields
    if (!formData.title || !formData.title.trim()) {
      newErrors.title = true;
      hasErrors = true;
    }

    if (!formData.description || isDescriptionEmpty(formData.description)) {
      newErrors.description = true;
      hasErrors = true;
    }

    // Validate URL if provided
    if (formData.card_link && formData.card_link.trim() !== '' && !isValidUrl(formData.card_link)) {
      newErrors.card_link = true;
      hasErrors = true;
    }

    // If there are validation errors, set them and stop
    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    // Format payload to match API requirements
    const payload = {
      announcement_id: formData.announcement_id,
      title: formData.title || "",
      sub_title: formData.subTitle || "",
      description: formData.description || "",
      card_link: (formData.card_link && formData.card_link.trim() !== '' && isValidUrl(formData.card_link)) ? formData.card_link : "",
      role_ids: "", // You can modify this based on your requirements
      valid_up_to: getValidUptoPayload(),
      is_active: Boolean(formData.active),
      is_web_url: Boolean(formData.isWebUrl),
      updated_by: userData.user_id,
      token: token
    };
    // Handle image_url based on whether it's a web URL or uploaded file
    if (formData.imageFile) {
      // If there's a file upload, include it in the payload
      payload.imageFile = formData.imageFile;
      payload.image_url = ""; // Will be set by the server after upload
    } else if (formData.isWebUrl && formData.image_url) {
      // If it's a web URL, use the provided URL
      payload.image_url = formData.image_url;
    } else {
      // Keep existing image or empty string
      const existingImageUrl = announcementToEdit?.image_url || "";
      if (existingImageUrl && !existingImageUrl.startsWith('blob:') && !existingImageUrl.includes('/AnnouncementImages/blob:')) {
        // Clean the existing image URL before using it
        const cleanedExistingUrl = cleanImageUrl(existingImageUrl);
        payload.image_url = cleanedExistingUrl || "";
      } else {
        payload.image_url = "";
      }
    }
    dispatch(announcementsUpdate(payload))
      .then((response) => {
        if (response.payload && response.payload.return_code === 0) {
          setSuccessMessage(response.payload.return_message || 'Announcement updated successfully');
          setShowSuccessModal(true);
          // Don't call onUpdate immediately - let the success modal handle it
        }
      })
      .catch((error) => {
        console.error("Error updating announcement:", error);
      });
  }, [formData, dispatch, onFinish, announcementToEdit, isDescriptionEmpty, onUpdate, userData.user_id, token, getValidUptoPayload]);




  const handleCancel = useCallback(() => {
    if (onFinish) onFinish();
  }, [onFinish]);

  return (
    <div>
      <div className="d-flex h-100 pt-2" style={{ gap: '20px' }}>
        {/* Left Panel - New Blog Form */}
        <div className="flex-grow-1" style={{ minWidth: '0', flex: '1 1 50%' }}>
          <h5 className="mb-4 blog-panel-title">{isEditor ? 'Update Blog' : 'New Blog'}</h5>
          <div className="bg-white rounded shadow">
            <div className="p-4">
              <div className="mb-3">
                <label className="form-label" style={{ fontFamily: 'Arial, sans-serif', fontWeight: 'bold' }}>Title</label>
                <input
                  type="text"
                  className={`form-control ${errors.title ? 'error-border' : ''}`}
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Title"
                />
              </div>

              <div className="mb-3">
                <label className="form-label" style={{ fontFamily: 'Arial, sans-serif', fontWeight: 'bold' }}>Sub Title</label>
                <input
                  type="text"
                  className="form-control"
                  name="subTitle"
                  value={formData.subTitle}
                  onChange={handleInputChange}
                  placeholder="Sub Title"
                />
              </div>

              <div className="mb-3 d-flex align-items-center">
                <label className="form-label mb-0 me-2" style={{ fontFamily: 'Arial, sans-serif', fontWeight: 'bold' }}>Is web url</label>
                <div className="form-check form-switch blog-form-switch">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    name="isWebUrl"
                    checked={formData.isWebUrl}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {formData.isWebUrl ? (
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    name="image_url"
                    placeholder="Image Web Url"
                    value={formData.image_url}
                    onChange={handleInputChange}
                  />
                </div>
              ) : (
                <div className="mb-3">
                  <button
                    type="button"
                    className="btn btn-primary back-button"
                    onClick={() => document.getElementById("imageUpload").click()}
                  >
                    Upload
                  </button>
                  <input
                    type="file"
                    id="imageUpload"
                    name="imagePreview"
                    onChange={handleImageUpload}
                    className="d-none"
                    accept=".jpg,.jpeg,.png,.gif"
                  />
                  <div className="mt-2">
                    <small className="text-muted">Allowed JPG, GIF or PNG. Max size of 800kB</small>
                  </div>
                  {formData.imagePreview && (
                    <div className="mt-3">
                      <img
                        src={formData.imagePreview}
                        alt=""
                        className="img-fluid rounded"
                        style={{ maxHeight: '200px', maxWidth: '100%' }}
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="mb-3">
                <label className="form-label" style={{ fontFamily: 'Arial, sans-serif', fontWeight: 'bold' }}>Description</label>
                <div style={{ width: '100%', minWidth: '0' }}>
                  <Editor
                    value={formData.description}
                    onChange={handleEditorChange}
                    editorWidth="100%"
                    editorHeight={300}
                  />
                </div>
                {errors.description && (
                  <div className='fw-bold' style={{ color: 'var(--pmr-danger)', fontSize: '0.875em', marginTop: '0.25rem', display: 'flex', alignItems: 'center' }}>
                    {/* <i className="fa fa-exclamation-circle" style={{ marginRight: '5px' }}></i> */}
                    Please enter the description.
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label" style={{ fontFamily: 'Arial, sans-serif', fontWeight: 'bold' }}>Announcement Link</label>
                <input
                  type="text"
                  className="form-control"
                  name="card_link"
                  value={formData.card_link}
                  onChange={handleInputChange}
                  placeholder="Announcement Link"
                />
                {errors.card_link && (
                  <div className='fw-bold' style={{ color: 'var(--pmr-danger)', fontSize: '0.875em', marginTop: '0.25rem', display: 'flex', alignItems: 'center' }}>
                    Please enter a valid URL (e.g., https://example.com).
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label" style={{ fontFamily: 'Arial, sans-serif', fontWeight: 'bold' }}>Valid Upto</label>
                <Flatpickr
                  key={`valid-upto-${announcementToEdit?.announcement_id ?? 'new'}-${effectiveValidUpto?.getTime() ?? 'empty'}`}
                  value={date ?? effectiveValidUpto}
                  onChange={handleDateChange}
                  options={flatpickrOptions}
                  render={({ value, ...props }, ref) => {
                    const displayDate = date ?? effectiveValidUpto;
                    return (
                      <input
                        {...props}
                        ref={ref}
                        value={displayDate ? formatDate(displayDate) : ""}
                        readOnly
                        className="form-control"
                        placeholder="Valid Upto"
                      />
                    );
                  }}
                />
              </div>

              <div className="d-flex justify-content-between align-items-center">
                <div className="form-check form-switch blog-form-switch">
                  <label className="form-check-label">Active</label>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <button
                    className="btn btn-primary me-2 back-button"
                    onClick={isEditor ? handleUpdate : handleSave}
                  >
                    {isEditor ? 'Update' : 'Save'}
                  </button>
                  <button
                    className="btn btn-danger back-button"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Blog Preview */}
        <div style={{ width: '50%', minWidth: '400px', flexShrink: '0' }}>
          <h5 className="mb-4 text-center blog-panel-title">Blog Preview</h5>
          <div className="bg-white rounded shadow">
            <div className="p-4">
              <h5 className='blog-card-title' style={{
                fontSize: '1.75rem',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'normal',
                lineHeight: '1.2',
                marginBottom: '0.5rem'
              }}>{formData.title || "Title"}</h5>
              <p className="text-muted" style={{
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'normal',
                lineHeight: '1.4',
                marginBottom: '1rem'
              }}>{formData.subTitle || "Sub Title"}</p>
              {formData.imagePreview && (
                <div className='d-flex justify-content-center'>
                  <img
                    src={formData.imagePreview}
                    alt=""
                    className="img-fluid rounded mb-3"
                    style={{ maxHeight: '250px', width: 'auto', objectFit: 'contain' }}
                  />
                </div>
              )}
              <div
                className="description"
                style={{
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  lineHeight: '1.6'
                }}
                dangerouslySetInnerHTML={{
                  __html: isDescriptionEmpty(formData.description) ? "Description" : formData.description
                }}
              />
              {formData.card_link && formData.card_link.trim() !== '' && isValidUrl(formData.card_link) && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <a href={formData.card_link} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--pmr-primary)', fontFamily: 'Arial, sans-serif', fontWeight: 'bold', textDecoration: 'none' }} >Announcement Link</a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <CustomSuccessModal
        show={showSuccessModal}
        onHide={() => {
          setShowSuccessModal(false);
          setSuccessMessage('');
          // Refresh announcements after modal closes
          if (onUpdate) {
            onUpdate();
          }
          if (onFinish) onFinish(); // Call onFinish after modal closes
        }}
        message={successMessage}
      />

      <style>{`
        .error-border { 
          border: 1px solid #dc3545 !important; 
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important; 
        }
        
        @media (max-width: 1200px) {
          .d-flex.h-100 {
            flex-direction: column !important;
          }
          
          .d-flex.h-100 > div:first-child {
            flex: 1 1 100% !important;
            margin-bottom: 20px;
          }
          
          .d-flex.h-100 > div:last-child {
            width: 100% !important;
            min-width: auto !important;
          }
        }
      `}</style>
    </div>
  );
};

export default BlogTwo;